import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { authAPI, workspaceAPI, articleAPI, templateAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from '../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { format } from 'date-fns';
import NotificationBell from '../components/NotificationBell';
import { BriefFieldEditor } from '../components/RichTextEditor';
import {
  Plus,
  FileText,
  Settings,
  LogOut,
  MoreVertical,
  Copy,
  Trash2,
  ChevronDown,
  LayoutGrid,
  FileStack,
  ClipboardList,
  PenLine,
  AlertCircle,
  User,
  Loader2,
  Search
} from 'lucide-react';

const STATUS_CONFIG = {
  creating_brief: { label: 'Creating Brief', color: 'bg-slate-100 text-slate-700' },
  brief_ready: { label: 'Brief Ready', color: 'bg-blue-100 text-blue-700' },
  brief_under_review: { label: 'Brief Under Review', color: 'bg-indigo-100 text-indigo-700' },
  ready_to_write: { label: 'Ready to Write', color: 'bg-cyan-100 text-cyan-700' },
  writing_content: { label: 'Writing', color: 'bg-amber-100 text-amber-700' },
  on_hold: { label: 'On Hold', color: 'bg-gray-100 text-gray-700' },
  delayed: { label: 'Delayed', color: 'bg-red-100 text-red-700' },
  ready_for_review: { label: 'Ready for Review', color: 'bg-purple-100 text-purple-700' },
  revision_requested: { label: 'Revision Requested', color: 'bg-orange-100 text-orange-700' },
  editing: { label: 'Editing', color: 'bg-amber-100 text-amber-700' },
  finalized: { label: 'Finalized', color: 'bg-emerald-100 text-emerald-700' },
};

// Template types - REMOVED "objective"
const TEMPLATE_TYPES = {
  seo_instructions: { label: 'SEO Guidelines', color: 'template-seo' },
  writing_guidelines: { label: 'Writing Guidelines', color: 'template-writing' },
};

export default function Dashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [workspaces, setWorkspaces] = useState([]);
  const [selectedWorkspace, setSelectedWorkspace] = useState(null);
  const [articles, setArticles] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchTimeout, setSearchTimeout] = useState(null);
  
  const [showCreateWorkspace, setShowCreateWorkspace] = useState(false);
  const [showCreateArticle, setShowCreateArticle] = useState(false);
  const [showCreateTemplate, setShowCreateTemplate] = useState(false);
  const [showManageTemplates, setShowManageTemplates] = useState(false);
  const [showDeleteArticle, setShowDeleteArticle] = useState(null);
  
  const [newWorkspaceName, setNewWorkspaceName] = useState('');
  const [newArticleTitle, setNewArticleTitle] = useState('');
  const [newTemplateName, setNewTemplateName] = useState('');
  const [newTemplateField, setNewTemplateField] = useState('');
  const [newTemplateContent, setNewTemplateContent] = useState('');
  const [editingTemplate, setEditingTemplate] = useState(null);

  const loadData = useCallback(async () => {
    try {
      const userRes = await authAPI.getMe();
      setUser(userRes.data);
      
      const wsRes = await workspaceAPI.getAll();
      setWorkspaces(wsRes.data);
      
      if (wsRes.data.length > 0) {
        const ws = wsRes.data[0];
        setSelectedWorkspace(ws);
        
        const [articlesRes, analyticsRes, templatesRes] = await Promise.all([
          articleAPI.getAll(ws.workspace_id),
          workspaceAPI.getAnalytics(ws.workspace_id),
          templateAPI.getAll(ws.workspace_id)
        ]);
        
        setArticles(articlesRes.data);
        setAnalytics(analyticsRes.data);
        setTemplates(templatesRes.data);
      }
    } catch (error) {
      if (error.response?.status === 401) {
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => { loadData(); }, [loadData]);

  // Debounced search
  const handleSearch = (query) => {
    setSearchQuery(query);
    if (searchTimeout) clearTimeout(searchTimeout);
    const timeout = setTimeout(async () => {
      if (selectedWorkspace) {
        try {
          const res = await articleAPI.getAll(selectedWorkspace.workspace_id, query || null);
          setArticles(res.data);
        } catch (error) { /* ignore */ }
      }
    }, 300);
    setSearchTimeout(timeout);
  };

  const loadWorkspaceData = async (workspace) => {
    try {
      const [articlesRes, analyticsRes, templatesRes] = await Promise.all([
        articleAPI.getAll(workspace.workspace_id),
        workspaceAPI.getAnalytics(workspace.workspace_id),
        templateAPI.getAll(workspace.workspace_id)
      ]);
      setArticles(articlesRes.data);
      setAnalytics(analyticsRes.data);
      setTemplates(templatesRes.data);
    } catch (error) {
      toast.error('Failed to load workspace data');
    }
  };

  const handleWorkspaceChange = async (workspace) => {
    setSelectedWorkspace(workspace);
    await loadWorkspaceData(workspace);
  };

  const handleCreateWorkspace = async () => {
    if (!newWorkspaceName.trim()) {
      toast.error('Please enter a workspace name');
      return;
    }
    try {
      const res = await workspaceAPI.create({ name: newWorkspaceName });
      setWorkspaces([...workspaces, res.data]);
      setSelectedWorkspace(res.data);
      setNewWorkspaceName('');
      setShowCreateWorkspace(false);
      toast.success('Workspace created');
      await loadWorkspaceData(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create workspace');
    }
  };

  const handleCreateArticle = async () => {
    if (!newArticleTitle.trim()) {
      toast.error('Please enter an article title');
      return;
    }
    try {
      const res = await articleAPI.create({
        workspace_id: selectedWorkspace.workspace_id,
        title: newArticleTitle
      });
      setArticles([res.data, ...articles]);
      setNewArticleTitle('');
      setShowCreateArticle(false);
      toast.success('Article created');
      navigate(`/article/${res.data.article_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create article');
    }
  };

  const handleDuplicateArticle = async (articleId) => {
    try {
      const res = await articleAPI.duplicate(articleId);
      setArticles([res.data, ...articles]);
      toast.success('Article duplicated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to duplicate article');
    }
  };

  const handleDeleteArticle = async () => {
    if (!showDeleteArticle) return;
    try {
      await articleAPI.delete(showDeleteArticle);
      setArticles(articles.filter(a => a.article_id !== showDeleteArticle));
      setShowDeleteArticle(null);
      toast.success('Article deleted');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete article');
    }
  };

  const handleCreateTemplate = async () => {
    if (!newTemplateName.trim() || !newTemplateField || !newTemplateContent.trim()) {
      toast.error('Please fill all template fields');
      return;
    }
    try {
      const res = await templateAPI.create(selectedWorkspace.workspace_id, {
        name: newTemplateName,
        target_field: newTemplateField,
        content: newTemplateContent
      });
      setTemplates([...templates, res.data]);
      setNewTemplateName('');
      setNewTemplateField('');
      setNewTemplateContent('');
      setShowCreateTemplate(false);
      toast.success('Template created');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create template');
    }
  };

  const handleUpdateTemplate = async () => {
    if (!editingTemplate) return;
    try {
      await templateAPI.update(
        selectedWorkspace.workspace_id,
        editingTemplate.template_id,
        { name: editingTemplate.name, content: editingTemplate.content }
      );
      setTemplates(templates.map(t => t.template_id === editingTemplate.template_id ? editingTemplate : t));
      setEditingTemplate(null);
      toast.success('Template updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update template');
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    try {
      await templateAPI.delete(selectedWorkspace.workspace_id, templateId);
      setTemplates(templates.filter(t => t.template_id !== templateId));
      toast.success('Template deleted');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete template');
    }
  };

  const handleLogout = async () => {
    try { await authAPI.logout(); navigate('/login'); } 
    catch (error) { navigate('/login'); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-white border-b border-border sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-8">
              <h1 className="text-xl font-bold text-primary">Wricoo</h1>
              
              <nav className="hidden md:flex items-center gap-1">
                {/* Workspace Dropdown */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="gap-2">
                      <LayoutGrid className="w-4 h-4" />
                      Workspace
                      <ChevronDown className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-56">
                    <DropdownMenuItem onClick={() => setShowCreateWorkspace(true)}>
                      <Plus className="w-4 h-4 mr-2" />
                      Create Workspace
                    </DropdownMenuItem>
                    {selectedWorkspace && (
                      <DropdownMenuItem onClick={() => navigate(`/workspace/${selectedWorkspace.workspace_id}/settings`)}>
                        <Settings className="w-4 h-4 mr-2" />
                        Manage Workspace
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>

                {/* Templates Dropdown - REMOVED Objective */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="gap-2">
                      <FileStack className="w-4 h-4" />
                      Templates
                      <ChevronDown className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-56">
                    <DropdownMenuSub>
                      <DropdownMenuSubTrigger>
                        <Plus className="w-4 h-4 mr-2" />
                        Create Template
                      </DropdownMenuSubTrigger>
                      <DropdownMenuSubContent>
                        <DropdownMenuItem onClick={() => {
                          setNewTemplateField('seo_instructions');
                          setShowCreateTemplate(true);
                        }}>
                          SEO Guidelines
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => {
                          setNewTemplateField('writing_guidelines');
                          setShowCreateTemplate(true);
                        }}>
                          Writing Guidelines
                        </DropdownMenuItem>
                      </DropdownMenuSubContent>
                    </DropdownMenuSub>
                    <DropdownMenuItem onClick={() => setShowManageTemplates(true)}>
                      <Settings className="w-4 h-4 mr-2" />
                      Manage Templates
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </nav>
            </div>

            <div className="flex items-center gap-4">
              {workspaces.length > 0 && (
                <Select
                  value={selectedWorkspace?.workspace_id || ''}
                  onValueChange={(value) => {
                    const ws = workspaces.find(w => w.workspace_id === value);
                    if (ws) handleWorkspaceChange(ws);
                  }}
                >
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Select workspace" />
                  </SelectTrigger>
                  <SelectContent>
                    {workspaces.map(ws => (
                      <SelectItem key={ws.workspace_id} value={ws.workspace_id}>{ws.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

              <NotificationBell />

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="rounded-full">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                      {user?.picture ? (
                        <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
                      ) : (
                        <User className="w-4 h-4 text-primary" />
                      )}
                    </div>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-3 py-2 border-b">
                    <p className="font-medium text-sm">{user?.name}</p>
                    <p className="text-xs text-muted-foreground">{user?.email}</p>
                  </div>
                  <DropdownMenuItem onClick={() => navigate('/settings')}>
                    <User className="w-4 h-4 mr-2" />
                    Account Settings
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleLogout}>
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {workspaces.length === 0 ? (
          <div className="text-center py-16">
            <LayoutGrid className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Welcome to Wricoo</h2>
            <p className="text-muted-foreground mb-6">Create your first workspace to get started</p>
            <Button onClick={() => setShowCreateWorkspace(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Create Workspace
            </Button>
          </div>
        ) : (
          <>
            {/* Analytics Cards */}
            {analytics && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                <div className="dashboard-card">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 rounded-lg bg-primary/10">
                      <ClipboardList className="w-5 h-5 text-primary" />
                    </div>
                    <span className="text-sm font-medium text-muted-foreground">Progress Report</span>
                  </div>
                  <div className="dashboard-card-value">{analytics.progress_report.not_finalized}</div>
                  <p className="dashboard-card-label">Articles not finalized</p>
                  <p className="dashboard-card-subtext">
                    {analytics.progress_report.writing} Writing · {analytics.progress_report.review} Review · {analytics.progress_report.on_hold} On Hold
                  </p>
                </div>

                <div className="dashboard-card">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 rounded-lg bg-amber-100">
                      <PenLine className="w-5 h-5 text-amber-600" />
                    </div>
                    <span className="text-sm font-medium text-muted-foreground">Written by Writers</span>
                  </div>
                  <div className="dashboard-card-value">{analytics.writer_words.total.toLocaleString()}</div>
                  <p className="dashboard-card-label">Total words</p>
                  <p className="dashboard-card-subtext">Across {analytics.writer_words.active_articles} active articles</p>
                </div>

                <div className="dashboard-card">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 rounded-lg bg-red-100">
                      <AlertCircle className="w-5 h-5 text-red-600" />
                    </div>
                    <span className="text-sm font-medium text-muted-foreground">Action Needed</span>
                  </div>
                  <div className="dashboard-card-value">{analytics.action_needed.total}</div>
                  <p className="dashboard-card-label">Articles requiring attention</p>
                  <p className="dashboard-card-subtext">
                    {analytics.action_needed.brief_review} Brief Review · {analytics.action_needed.revision_requested} Revision · {analytics.action_needed.delayed} Delayed
                  </p>
                </div>
              </div>
            )}

            {/* Articles */}
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">Articles</h2>
              <Button onClick={() => setShowCreateArticle(true)} data-testid="create-article-btn">
                <Plus className="w-4 h-4 mr-2" />
                New Article
              </Button>
            </div>

            {/* Search Bar */}
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search articles by title, keyword, or URL slug..."
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                className="pl-9 bg-white"
              />
            </div>

            {articles.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-lg border border-border">
                <FileText className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground">No articles yet. Create your first article to get started.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {articles.map(article => (
                  <div
                    key={article.article_id}
                    className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md hover:border-blue-200 transition-all cursor-pointer group"
                    onClick={() => navigate(`/article/${article.article_id}`)}
                    data-testid={`article-card-${article.article_id}`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <h3 className="font-semibold text-foreground text-sm leading-snug line-clamp-2 flex-1 mr-2 group-hover:text-blue-600 transition-colors">{article.title}</h3>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild onClick={e => e.stopPropagation()}>
                          <Button variant="ghost" size="icon" className="shrink-0 h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity">
                            <MoreVertical className="w-3.5 h-3.5" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleDuplicateArticle(article.article_id); }}>
                            <Copy className="w-4 h-4 mr-2" />
                            Duplicate
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem className="text-destructive" onClick={(e) => { e.stopPropagation(); setShowDeleteArticle(article.article_id); }}>
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                    
                    <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-medium mb-3 ${STATUS_CONFIG[article.status]?.color || 'bg-gray-100 text-gray-700'}`}>
                      {STATUS_CONFIG[article.status]?.label || article.status}
                    </span>
                    
                    <div className="space-y-1.5 text-xs text-muted-foreground border-t border-gray-100 pt-3">
                      <div className="flex justify-between">
                        <span>Created</span>
                        <span className="font-medium text-foreground">{format(new Date(article.created_at), 'MMM d, yyyy')}</span>
                      </div>
                      {article.completed_at && (
                        <div className="flex justify-between">
                          <span>Completed</span>
                          <span className="font-medium text-emerald-600">{format(new Date(article.completed_at), 'MMM d, yyyy')}</span>
                        </div>
                      )}
                      {article.assigned_writer_name && (
                        <div className="flex justify-between">
                          <span>Writer</span>
                          <span className="font-medium text-foreground flex items-center gap-1">
                            <User className="w-3 h-3" />{article.assigned_writer_name}
                          </span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span>Writer Words</span>
                        <span className="font-medium text-foreground">{(article.writer_word_count || 0).toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Total Words</span>
                        <span className="font-medium text-foreground">{(article.total_word_count || 0).toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </main>

      {/* Create Workspace Dialog */}
      <Dialog open={showCreateWorkspace} onOpenChange={setShowCreateWorkspace}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Workspace</DialogTitle>
            <DialogDescription>Create a new workspace for your team</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div>
              <Label htmlFor="workspace-name">Workspace Name</Label>
              <Input
                id="workspace-name"
                value={newWorkspaceName}
                onChange={(e) => setNewWorkspaceName(e.target.value)}
                placeholder="My Content Team"
                className="mt-1.5"
              />
            </div>
            <Button onClick={handleCreateWorkspace} className="w-full">Create Workspace</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Article Dialog */}
      <Dialog open={showCreateArticle} onOpenChange={setShowCreateArticle}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Article</DialogTitle>
            <DialogDescription>Start a new article in this workspace</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div>
              <Label htmlFor="article-title">Article Title</Label>
              <Input
                id="article-title"
                value={newArticleTitle}
                onChange={(e) => setNewArticleTitle(e.target.value)}
                placeholder="Enter article title"
                className="mt-1.5"
              />
            </div>
            <Button onClick={handleCreateArticle} className="w-full">Create Article</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Template Dialog - Using Rich Text Editor */}
      <Dialog open={showCreateTemplate} onOpenChange={setShowCreateTemplate}>
        <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle>Create Template</DialogTitle>
            <DialogDescription>
              Create a template for: {TEMPLATE_TYPES[newTemplateField]?.label || newTemplateField}
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto space-y-4 pt-4 pr-2">
            <div>
              <Label htmlFor="template-name">Template Name</Label>
              <Input
                id="template-name"
                value={newTemplateName}
                onChange={(e) => setNewTemplateName(e.target.value)}
                placeholder="e.g., Blog Post Guidelines"
                className="mt-1.5"
              />
            </div>
            <div>
              <Label>Template Content</Label>
              <div className="mt-1.5 border border-input rounded-md">
                <BriefFieldEditor
                  content={newTemplateContent}
                  onChange={setNewTemplateContent}
                  placeholder="Enter template content..."
                />
              </div>
            </div>
          </div>
          <div className="flex-shrink-0 pt-4 border-t">
            <Button onClick={handleCreateTemplate} className="w-full">Create Template</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Manage Templates Dialog */}
      <Dialog open={showManageTemplates} onOpenChange={setShowManageTemplates}>
        <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle>Manage Templates</DialogTitle>
            <DialogDescription>View and edit your templates ({templates.length}/10)</DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto space-y-4 pt-4 pr-2">
            {templates.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <FileStack className="w-10 h-10 mx-auto mb-3 opacity-50" />
                <p>No templates yet. Create your first template from the menu.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {templates.map(template => (
                  <div key={template.template_id} className={`p-4 rounded-lg border ${TEMPLATE_TYPES[template.target_field]?.color || 'border-border'}`}>
                    {editingTemplate?.template_id === template.template_id ? (
                      <div className="space-y-3">
                        <Input
                          value={editingTemplate.name}
                          onChange={(e) => setEditingTemplate({...editingTemplate, name: e.target.value})}
                          placeholder="Template name"
                        />
                        <div className="border border-input rounded-md">
                          <BriefFieldEditor
                            content={editingTemplate.content}
                            onChange={(content) => setEditingTemplate({...editingTemplate, content})}
                            placeholder="Template content..."
                          />
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" onClick={handleUpdateTemplate}>Save</Button>
                          <Button size="sm" variant="ghost" onClick={() => setEditingTemplate(null)}>Cancel</Button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium">{template.name}</span>
                            <span className={`px-2 py-0.5 rounded text-xs ${TEMPLATE_TYPES[template.target_field]?.color}`}>
                              {TEMPLATE_TYPES[template.target_field]?.label}
                            </span>
                          </div>
                          <div className="text-sm text-muted-foreground line-clamp-2" 
                               dangerouslySetInnerHTML={{ __html: template.content.substring(0, 200) }} />
                        </div>
                        <div className="flex gap-1 ml-4">
                          <Button size="sm" variant="ghost" onClick={() => setEditingTemplate(template)}>Edit</Button>
                          <Button size="sm" variant="ghost" className="text-destructive" onClick={() => handleDeleteTemplate(template.template_id)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Article Confirmation */}
      <AlertDialog open={!!showDeleteArticle} onOpenChange={() => setShowDeleteArticle(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Article</AlertDialogTitle>
            <AlertDialogDescription>Are you sure you want to delete this article? This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteArticle} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
